/**
 * `liveReviewDraft` — riduttore puro della bozza del `LiveReviewWizard`.
 *
 * La bozza modella i Review_Score in composizione come
 * `Map<skillId, Livello | null>`, lo stesso schema usato da `ReviewPanel`:
 * ogni voce associa una skill all'ultimo Livello assegnato dal Reviewer in
 * questo invio, oppure `null` quando la skill non è (più) votata. La submission
 * invia poi le sole skill con `livello !== null`.
 *
 * Questa logica è isolata da React e priva di dipendenze esterne, così da
 * poter essere verificata in isolamento (Property 2 e Property 3 del design):
 * - **Property 2**: assegnare un Livello registra il Review_Score; a parità di
 *   skill l'ultima assegnazione prevale.
 * - **Property 3**: come Review_Score sono accettati esclusivamente i Livelli
 *   della scala `e-1..e-5` (`e-0` escluso, coerente con il backend).
 *
 * _Requirements: 2.3, 2.4_
 */
import { isLivello, type Livello } from '../shared/dto';

/** Bozza dei Review_Score: skillId → Livello votato, o `null` se non votato. */
export type LiveReviewDraft = Map<string, Livello | null>;

/**
 * Azione sul riduttore della bozza.
 *
 * - `assign`: assegna `livello` alla skill `skillId`. Accettati solo i Livelli
 *   `e-1..e-5`; un Livello fuori scala (incluso `e-0`) lascia la bozza
 *   invariata. L'ultima assegnazione valida per una skill prevale.
 * - `clear`: rimuove il voto della skill `skillId` (deselezione), riportandola
 *   allo stato non votato (`null`), coerente con l'annullamento riga di
 *   `ReviewPanel`.
 */
export type LiveReviewDraftAction =
  | { type: 'assign'; skillId: string; livello: Livello }
  | { type: 'clear'; skillId: string };

/**
 * Type guard dei Livelli ammessi come Review_Score: i soli `e-1..e-5`.
 * Riusa `isLivello` condiviso ed esclude `e-0`, allineandosi alla regola di
 * dominio del backend (`isLivello(x) && x !== 'e-0'`).
 */
export function isReviewLivello(value: unknown): value is Livello {
  return isLivello(value) && value !== 'e-0';
}

/**
 * Riduttore puro della bozza: dato lo stato corrente e un'azione, restituisce
 * una **nuova** bozza senza mutare quella in ingresso.
 *
 * Un'azione `assign` con un Livello non ammesso (fuori da `e-1..e-5`, quindi
 * anche `e-0`) non altera lo stato: la bozza corrente è restituita invariata.
 */
export function liveReviewDraftReducer(
  draft: LiveReviewDraft,
  action: LiveReviewDraftAction,
): LiveReviewDraft {
  switch (action.type) {
    case 'assign': {
      // Scarta i Livelli fuori scala: la bozza resta invariata (Property 3).
      if (!isReviewLivello(action.livello)) return draft;
      // L'ultima assegnazione per la skill prevale (Property 2).
      const next = new Map(draft);
      next.set(action.skillId, action.livello);
      return next;
    }
    case 'clear': {
      // Deselezione: la skill torna allo stato non votato (`null`).
      const next = new Map(draft);
      next.set(action.skillId, null);
      return next;
    }
    default: {
      // Esaustività: ogni variante di azione è gestita sopra.
      return draft;
    }
  }
}

/**
 * Estrae dalla bozza i soli Review_Score effettivamente votati
 * (`livello !== null`), nel formato inviato dalla submission. È la vista della
 * bozza usata all'invio del batch.
 */
export function draftToScores(
  draft: LiveReviewDraft,
): { skillId: string; livello: Livello }[] {
  const scores: { skillId: string; livello: Livello }[] = [];
  for (const [skillId, livello] of draft) {
    if (livello !== null) scores.push({ skillId, livello });
  }
  return scores;
}
